import { useEffect, useState } from 'react';
import { useNavBehavior } from 'components/Navigation/helpers/hooks';

const SCROLL_THRESHOLD = 5; // Consider "at top" if within 5px
const TOP_STICKY_THRESHOLD = 100; // Once at top, stay visible until scrolling down past this

export const useExploreTabsBehavior = (): { isHide: boolean; bgColor?: string } => {
  const { bgColor } = useNavBehavior();

  // Start visible on initial page load
  const [isHide, setIsHide] = useState(false);

  useEffect(() => {
    let lastScrollValue = typeof window !== 'undefined' ? window.scrollY : 0;
    let hasScrolledPastThreshold = lastScrollValue > SCROLL_THRESHOLD;
    let hasReachedTop = lastScrollValue <= SCROLL_THRESHOLD;

    const handleScroll = () => {
      const scrollToTop = Math.max(0, window.scrollY); // Clamp to 0 to handle negative overscroll
      const isScrollingUp = scrollToTop < lastScrollValue;
      const isAtTop = scrollToTop <= SCROLL_THRESHOLD;

      // Track if user has scrolled past threshold
      if (scrollToTop > SCROLL_THRESHOLD) {
        hasScrolledPastThreshold = true;
      }

      // Track when we've reached the top (scrolling up and at top)
      if (isScrollingUp && isAtTop) {
        hasReachedTop = true;
      }

      // Once we've reached the top, keep tabs visible until scrolling down significantly
      // This prevents tabs from disappearing due to overscroll bounce
      if (hasReachedTop && scrollToTop <= TOP_STICKY_THRESHOLD) {
        setIsHide(false);
      } else if (hasScrolledPastThreshold) {
        // If we've scrolled down past the sticky threshold, hide tabs
        // Show tabs only when scrolling up and at top
        setIsHide(!(isScrollingUp && isAtTop));
        // Reset reachedTop if we've scrolled down significantly
        if (scrollToTop > TOP_STICKY_THRESHOLD) {
          hasReachedTop = false;
        }
      } else {
        // Keep visible if at top and hasn't scrolled past threshold yet
        setIsHide(false);
      }

      lastScrollValue = scrollToTop;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return {
    isHide,
    bgColor,
  };
};
